import React, {useCallback} from 'react';
import './flowAnimation.css';
import solarSvg from '../../../assets/rebranding/icons/icon_solar.svg'
import factorySvg from '../../../assets/rebranding/icons/icon_industry.svg'
import netSvg from '../../../assets/rebranding/icons/icon_net.svg'
import batterySvg from '../../../assets/rebranding/icons/icon_battery.svg'
import {useTheme} from "@mui/material";
import { CustomTheme } from '../../../config/theme/theme';
import BatteryAnimation from './batteryAnimation/batteryAnimation';
import {Simulate} from "react-dom/test-utils";

interface FlowAnimationProps {
  topLeft?: number;
  topRight?: number;
  bottomLeft?: number;
  bottomRight?: number;
  vertical?: number;
  horizontal?: number;

  topValue?: number;
  rightValue?: number;
  bottomValue?: number;
  leftValue?: number;

  viewRatio?: number;
}

const FlowAnimation: React.FC<FlowAnimationProps> = ({
                                                       topLeft = 0,
                                                       topRight = 0,
                                                       bottomLeft = 0,
                                                       bottomRight = 0,
                                                       vertical = 0,
                                                       horizontal = 0,
                                                       topValue = 0,
                                                       rightValue = 0,
                                                       bottomValue = 0,
                                                       leftValue = 0,
                                                       viewRatio = 2
                                                     }) => {


  let theme: CustomTheme = useTheme()

  // These parameters control most of the size of the SVG
  const animationDuration = 12;
  const animationTimings: number[] = [0, 3, 6, 9]
  const svgHeight = 40;
  const iconWidth = 22;
  const lineSpacing = 2;
  const arrowSize = 1;
  const paddingX = 0;
  const paddingY = 0;


  // These 2 parameters control the shape of the SVG
  const middleX = paddingX + (svgHeight * viewRatio);
  const middleY = paddingY + svgHeight;

  // These 2 parameters keep the symmetry
  const rightX = (middleX - paddingX) * 2;
  const bottomY = (middleY - paddingY) * 2;

  // Viewbox with automatic padding of 0.25
  const viewBox = `0 0 ${middleX * 2} ${middleY * 2}`

  // These parameters determine the size of the arrows running along the path
  const arrowPoints: string = `-${arrowSize},${0.8 * arrowSize} ${arrowSize},0 -${arrowSize},-${0.8 * arrowSize}`;
  const inverseArrowPoints: string = `-${arrowSize},0 ${arrowSize},${0.8 * arrowSize} ${arrowSize},-${0.8 * arrowSize}`;

  // Everything should be controlled with the above parameters. This should not be touched
  const verticalPath: string = `M ${middleX} ${paddingY + iconWidth} V ${bottomY - iconWidth + paddingY}`;
  const horizontalPath: string = `M ${paddingX + iconWidth} ${middleY} H ${rightX - iconWidth + paddingX}`;
  const topLeftPath: string = `M ${paddingX + iconWidth - 1} ${middleY - lineSpacing} H ${middleX - lineSpacing - 1} A 1 1 0 0 0 ${middleX - lineSpacing} ${middleY - lineSpacing - 1} V ${paddingY + iconWidth - 1}`;
  const topRightPath: string = `M ${middleX + lineSpacing} ${paddingY + iconWidth - 1} V ${middleY - lineSpacing - 1} A 1 1 0 0 0 ${middleX + lineSpacing + 1} ${middleY - lineSpacing} H ${rightX - iconWidth + paddingX + 1}`;
  const bottomLeftPath: string = `M ${paddingX + iconWidth - 1} ${middleY + lineSpacing} H ${middleX - lineSpacing - 1} A 1 1 0 0 1 ${middleX - lineSpacing} ${middleY + lineSpacing + 1} V ${bottomY - iconWidth + paddingY + 1}`;
  const bottomRightPath: string = `M ${middleX + lineSpacing} ${bottomY - iconWidth + paddingY + 1} V ${middleY + lineSpacing + 1} A 1 1 0 0 1 ${middleX + lineSpacing + 1} ${middleY + lineSpacing} H ${rightX - iconWidth + paddingX + 1}`;

  const renderIcon = useCallback((id, x, y, value, icon) => {
    return <>
      <svg id={id} x={x} y={y} width={iconWidth} height={iconWidth} version="1.1"
           xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
        <circle cx={50} cy={50} r="49" stroke={theme.palette.text.tertiary} strokeWidth="1" fill="white"/>
        <foreignObject x="25" y="15" width="50" height="50">
          {icon}
        </foreignObject>
        <text x="50" y="80" fontSize="12" dominantBaseline="middle" textAnchor="middle" fill={theme.palette.text.primary}>{value} kW
        </text>
      </svg>
    </>
  }, [topLeft, topRight, bottomLeft, bottomRight, vertical, horizontal,
    topValue, rightValue, bottomValue, leftValue])

  const renderPath = useCallback(
    (location, path) => {
      return (
        <>
          <>
            <path
              fill="none"
              stroke={location != 0 ? theme.palette.primary.main : "#CCCCCC"}
              strokeWidth={location != 0 ? 0.4 : 0.2}
              d={path}
              className={"test"}
            />
            {location != 0 && animationTimings.map((timing: number) => {
              return (
                <polygon key={timing} points={location == 1 ? arrowPoints : inverseArrowPoints} fill={theme.palette.primary.main}>
                  <animateMotion
                    dur={`${animationDuration}s`}
                    repeatCount="indefinite"
                    keyPoints={location == 1 ? "0;1" : "1;0"}
                    keyTimes="0;1"
                    calcMode="linear"
                    rotate="auto"
                    path={path}
                    begin={`${timing}s`}/>
                  {timing !== 0 &&
                      <animate attributeName="visibility" values="hidden;visible" dur={`${timing * 2}s`}/>}
                </polygon>
              )
            })}
          </>
        </>
      )
    }, [topLeft, topRight, bottomLeft, bottomRight, vertical, horizontal,
      topValue, rightValue, bottomValue, leftValue])

  return (
    <>
      <div style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center'}}>
        {/*<svg viewBox={viewBox} width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">*/}
        {/*  <path*/}
        {/*    fill="none"*/}
        {/*    stroke="lightgrey"*/}
        {/*    d={topLeftPath}*/}
        {/*  />*/}

        {/*  <polygon key="somekey" points={true ? arrowPoints : inverseArrowPoints} fill={theme.palette.primary.main}>*/}
        {/*    <animateMotion*/}
        {/*      dur={`${animationDuration}s`}*/}
        {/*      keyPoints={true ? "0;1" : "1;0"}*/}
        {/*      keyTimes="0;1"*/}
        {/*      calcMode="linear"*/}
        {/*      repeatCount="indefinite"*/}
        {/*      path={topLeftPath} />*/}
        {/*  </polygon>*/}
        {/*</svg>*/}
        <svg viewBox={viewBox} width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          {renderPath(topLeft, topLeftPath)}
          {renderPath(topRight, topRightPath)}
          {renderPath(bottomLeft, bottomLeftPath)}
          {renderPath(bottomRight, bottomRightPath)}
          {renderPath(horizontal, horizontalPath)}
          {renderPath(vertical, verticalPath)}

          {renderIcon("sun", middleX - (iconWidth / 2), paddingY, topValue, <img width="100%" height="100%" src={solarSvg}/>)}
          {renderIcon("battery", paddingX, middleY - (iconWidth / 2), leftValue, <img width="100%" height="100%" src={batterySvg}/>)}
          {/* Commented out battery animation logic - work in progress */}
          {/* {renderIcon("battery", paddingX, middleY - (iconWidth / 2), leftValue, <BatteryAnimation charging={leftValue < 0 ? -1 : leftValue > 0 ? 1 : 0}/>)} */}
          {renderIcon("load", rightX - iconWidth + paddingX, middleY - (iconWidth / 2), rightValue, <img width="100%" height="100%" src={factorySvg}/>)}
          {renderIcon("net", middleX - (iconWidth / 2), bottomY - iconWidth + paddingY, bottomValue, <img width="100%" height="100%" src={netSvg}/>)}
        </svg>
      </div>
    </>
  );
};

export default FlowAnimation;